import React from "react";
import "./style.scss";
import AddAPhotoIcon from "@material-ui/icons/AddAPhoto";
import { useInputImageId } from "./useInputImageId";
import { css, cx } from "emotion";
import CloseIcon from "@material-ui/icons/Close";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import AppService from "../../Services/AppService/AppService";
import CircularProgress from "@material-ui/core/CircularProgress";
import DeleteIcon from "@material-ui/icons/Delete";
import { IconButton } from "@material-ui/core";

function readFile(file) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => resolve(reader.result), false);
    reader.readAsDataURL(file);
  });
}

function resizeImage(base64Str, maxWidth = 300, maxHeight = 300) {
  return new Promise((resolve) => {
    let img = new Image();
    img.src = base64Str;
    img.onload = () => {
      let canvas = document.createElement("canvas");
      const MAX_WIDTH = maxWidth;
      const MAX_HEIGHT = maxHeight;
      let width = img.width;
      let height = img.height;

      if (width > height) {
        if (width > MAX_WIDTH) {
          height *= MAX_WIDTH / width;
          width = MAX_WIDTH;
        }
      } else {
        if (height > MAX_HEIGHT) {
          width *= MAX_HEIGHT / height;
          height = MAX_HEIGHT;
        }
      }
      canvas.width = width;
      canvas.height = height;
      let ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      let png64 = canvas.toDataURL("image/png", "0.4");
      resolve(png64);
    };
  });
}

const ImageUploader = React.forwardRef((props, ref) => {
  const {
    image,
    footerNote,
    errorNote,
    maxSize,
    aspectRatio,
    onUploadDone,
    onRemove,
  } = props;
  const refInputImage = React.useRef();
  const componentID = useInputImageId();
  const cropperRef = React.useRef(null);

  const [openImageCropper, setOpenImageCropper] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState("");
  const [croppedImage, setCroppedImage] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState("");
  const [isError, setIsError] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [availableImage, setAvailableImage] = React.useState(image);

  const uploadImage = (image) => {
    return new Promise((resolve) => {
      AppService.uploaderFile({ file: image })
        .then((response) => {
          setLoading(false);
          setIsError(false);
          setAvailableImage(response.data.url);
          onUploadDone(response.data.url);
          resolve(true);
        })
        .catch((error) => {
          let response = error.response;

          setLoading(false);
          setIsError(true);

          if (response.status === 500) {
            setErrorMessage(
              "Failed upload image, please compress your image to a smaller size"
            );
          } else {
            setErrorMessage(error.statusText);
          }

          resolve(false);
        });
    });
  };

  const handleToggleImageCropper = () => {
    setOpenImageCropper(!openImageCropper);
    refInputImage.current.value = null;
  };

  const handleOnChangeFile = async (event) => {
    setErrorMessage("");
    setIsError(false);

    if (event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file.size <= maxSize * 1024) {
        let imageDataUrl = await readFile(file);
        setImageSrc(imageDataUrl);
        setErrorMessage("");
        setIsError(false);
        setOpenImageCropper(true);
      } else {
        setErrorMessage("Max file size " + maxSize + "kb ");
        setIsError(true);
      }
    }
  };

  const onCrop = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    const cropRes = cropper.getCroppedCanvas().toDataURL();
    let width = 300;
    let height = 300;

    switch (aspectRatio) {
      case 16 / 9 || 16 / 9:
        width = 640;
        height = 360;
        break;

      default:
        width = 300;
        height = 300;
        break;
    }

    resizeImage(cropRes, width, height).then((result) => {
      setCroppedImage(result);
    });
  };

  const handleReset = () => {
    const imageElement = cropperRef?.current;
    const cropper = imageElement?.cropper;
    cropper.reset();
  };

  const handleClickRemove = () => {
    setAvailableImage("");
    setLoading(false);
    setIsError(false);
    setErrorMessage("");
    refInputImage.current.value = null;
    onRemove(availableImage);
  };

  const handleCropDone = () => {
    setAvailableImage("");
    setLoading(true);
    uploadImage(croppedImage).then(() => {
      handleToggleImageCropper();
    });
  };

  return (
    <div className="d-inline-block" ref={ref} style={{ maxWidth: 100 }}>
      <input
        type="file"
        key={componentID}
        className="input-file-image"
        id={`file-image-${componentID}`}
        accept="image/x-png,image/gif,image/jpeg"
        onChange={(event) => handleOnChangeFile(event)}
        ref={refInputImage}
      />
      <div className="input-image-box">
        {!loading && availableImage !== "" && (
          <IconButton
            aria-label="remove"
            className="btn-remove"
            size="small"
            onClick={() => handleClickRemove()}
          >
            <DeleteIcon />
          </IconButton>
        )}

        {availableImage === "" && (
          <label
            className="input-image-inner"
            htmlFor={`file-image-${componentID}`}
          >
            <AddAPhotoIcon style={{ width: 32, height: 32 }} />
            <span className="font-10 font-bold text-primary">Upload</span>
          </label>
        )}
        {availableImage !== "" && (
          <div className="input-image-inner">
            <img
              src={availableImage}
              alt="result"
              style={{ width: "100%", borderRadius: 8 }}
            />
          </div>
        )}
      </div>
      {footerNote && footerNote !== "" && (
        <label className="font-10 text-gray-757575 w-100 text-center">
          {footerNote}
        </label>
      )}
      {errorNote && errorNote !== "" && (
        <label className="font-10 text-error w-100 text-center">
          {errorNote}
        </label>
      )}
      {isError && (
        <label className="font-10 text-error w-100 text-center">
          {errorMessage}
        </label>
      )}
      <div
        className={cx(
          css`
            background: rgba(0, 0, 0, 0.3);
          `,
          openImageCropper
            ? "modal d-block fade-in-fwd"
            : "modal d-none fade-out-bck"
        )}
        role="dialog"
      >
        <div className="modal-dialog modal-dialog-centered" role="dialog">
          <div
            className={cx(
              css`
                background: none !important;
                border: none !important;
                z-index: 1500 !important;
              `,
              "modal-content modal-cropper-imageuploader"
            )}
          >
            <div className="card border-0 rounded-8px">
              <div className="card-header bg-white p-4 border-bottom-1-secondary">
                <div className="d-flex w-100 justify-content-between align-items-center">
                  <h2 className="font-bold font-20">Edit Image</h2>
                  <span
                    onClick={handleToggleImageCropper}
                    className="cursor-pointer"
                  >
                    <CloseIcon />
                  </span>
                </div>
              </div>
              {!loading && !isError && (
                <div className="card-body p-3">
                  <Cropper
                    src={imageSrc || ""}
                    style={{ width: "100%" }}
                    aspectRatio={aspectRatio ? aspectRatio : 13 / 16}
                    guides={false}
                    crop={onCrop}
                    onReset={onCrop}
                    ref={cropperRef}
                  />
                </div>
              )}
              {!loading && !isError && (
                <div className="card-body p-3 text-center">
                  <button
                    type="button"
                    className="btn btn-md text-gray-757575 bg-white border-0 font-12 font-bold px-5"
                    onClick={handleReset}
                  >
                    RESET
                  </button>
                  <button
                    type="button"
                    className={cx(
                      css`
                        background-color: #bbdefb !important;
                        color: #2196f3 !important;
                      `,
                      "btn btn-md border-0 font-10 font-bold px-5"
                    )}
                    onClick={handleCropDone}
                  >
                    DONE
                  </button>
                </div>
              )}
              {!loading && isError && (
                <div className="card-body p-3 text-center text-danger">
                  {errorMessage || "Error"}
                </div>
              )}
              {loading && (
                <div className="card-body p-3 text-center">
                  <CircularProgress />
                  <span className="d-block text-center text-gray-757575">
                    Upload image ...
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ImageUploader;
